import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';

export type RatePeriod = 'day' | 'week' | 'campaign' | 'send' | 'post';
export type MomentKind = 'sale' | 'seasonal' | 'gifting' | 'brand' | 'other';

export const PERIOD_LABEL: Record<RatePeriod, string> = {
  day: 'per day',
  week: 'per week',
  campaign: 'per campaign',
  send: 'per send',
  post: 'per post',
};

export const KIND_LABEL: Record<MomentKind, string> = {
  sale: 'Sale',
  seasonal: 'Seasonal',
  gifting: 'Gifting',
  brand: 'Brand moment',
  other: 'Other',
};

export interface PlacementRate {
  id: number;
  key: string;
  label: string;
  asset_type: string;
  /** Extra types sold together with asset_type as one item (packs). */
  pack_types: string[];
  category: string;
  /** Decimal as string, e.g. "200.00". */
  price: string;
  /** Most slots one brand may book of this line in a campaign (null = no max). */
  max_per_brand: number | null;
  /** Standard expected numbers per day (meaning: see lib/stats statLabelsFor). */
  stat_1: number | null;
  stat_2: number | null;
  period: RatePeriod;
  price_note: string;
  slots_per_moment: number;
  description: string;
  /** Optional "What we need from you" line for brands. */
  requirements: string;
  /** Standard mockup pictures — only in the list with `useRates(true)`. */
  example_images?: string[];
  image_count: number;
  active: boolean;
  sort_order: number;
  updated_at: string;
}

export interface BundleLine {
  rate: string;
  qty: number;
}

export interface RateBundle {
  id: number;
  key: string;
  name: string;
  price: string;
  blurb: string;
  lines: BundleLine[];
  highlight: boolean;
  active: boolean;
  sort_order: number;
  /** Sum of the lines at rate-card prices, decimal string. */
  list_value: string;
  updated_at: string;
}

export interface Moment {
  id: number;
  year: number;
  name: string;
  kind: MomentKind;
  start_date: string;
  end_date: string;
  /** Overrides only (rate key → count). */
  slots: Record<string, number>;
  /** Overrides merged with the rate-card defaults. */
  slots_effective: Record<string, number>;
  slots_total: number;
  /** rate key → slots already on brand deal sheets for this moment. */
  slots_sold: Record<string, number>;
  campaign: string | null;
  campaign_name?: string;
  notes: string;
  updated_at: string;
}

const RATES = ['rate-card', 'rates'] as const;
const BUNDLES = ['rate-card', 'bundles'] as const;
const momentsKey = (year: number) => ['moments', year] as const;

/** `withImages`: include the standard mockup pictures (heavy — only the
 *  Placements & prices page asks for them). */
export const useRates = (withImages = false) =>
  useQuery<PlacementRate[]>({
    queryKey: withImages ? [...RATES, 'images'] : RATES,
    queryFn: async () => (await axios.get(`api/rate-card/rates/${withImages ? '?images=1' : ''}`)).data,
  });

export const useBundles = () =>
  useQuery<RateBundle[]>({
    queryKey: BUNDLES,
    queryFn: async () => (await axios.get('api/rate-card/bundles/')).data,
  });

export const useMoments = (year: number) =>
  useQuery<Moment[]>({
    queryKey: momentsKey(year),
    queryFn: async () => (await axios.get(`api/moments/?year=${year}`)).data,
  });

type RateInput = Partial<Omit<PlacementRate, 'id' | 'updated_at'>>;
type BundleInput = Partial<Omit<RateBundle, 'id' | 'updated_at' | 'list_value'>>;
type MomentInput = Partial<Omit<Moment, 'id' | 'updated_at' | 'slots_effective' | 'slots_total' | 'campaign_name'>>;

function useCrud<T, I>(path: string, keys: readonly (readonly unknown[])[]) {
  const qc = useQueryClient();
  const invalidate = () => keys.forEach((k) => qc.invalidateQueries({ queryKey: k }));
  const create = useMutation({
    mutationFn: async (payload: I): Promise<T> => (await axios.post(path, payload)).data,
    onSuccess: invalidate,
  });
  const patch = useMutation({
    mutationFn: async ({ id, ...payload }: I & { id: number }): Promise<T> =>
      (await axios.patch(`${path}${id}/`, payload)).data,
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (id: number) => {
      await axios.delete(`${path}${id}/`);
    },
    onSuccess: invalidate,
  });
  return { create, patch, remove };
}

// Moments read the rate defaults, so a rate change refreshes them too.
export const useRateMutations = () =>
  useCrud<PlacementRate, RateInput>('api/rate-card/rates/', [RATES, BUNDLES, ['moments']]);
export const useBundleMutations = () =>
  useCrud<RateBundle, BundleInput>('api/rate-card/bundles/', [BUNDLES]);
export const useMomentMutations = () =>
  useCrud<Moment, MomentInput>('api/moments/', [['moments']]);

export const fmtEur = (value: string | number) => {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  if (!Number.isFinite(n)) return '—';
  return `€${n.toLocaleString('en-GB', { maximumFractionDigits: n % 1 === 0 ? 0 : 2 })}`;
};
